import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Background, BackgroundVariant, MiniMap, ReactFlow, type Edge, type NodeMouseHandler,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import '../styles/digital-twin.css';
import { Eye, EyeOff, Focus, Layers3, LoaderCircle, RefreshCw, Rows3, TriangleAlert } from 'lucide-react';
import { digitalTwinApi, DigitalTwinApiError } from '../lib/digitalTwinApi';
import { digitalTwinQueryKeys } from '../lib/digitalTwinQueryKeys';
import { buildDigitalTwinGraph, type DigitalTwinNode } from '../lib/digitalTwinGraph';
import { buildEquipmentTelemetry, isDigitalTwinDemoTelemetryAllowed } from '../lib/digitalTwinTelemetry';
import { iotApi, type IoTAlert, type IoTNode } from '../lib/iotApi';
import { useAuth } from '../contexts/AuthContext';
import type { Equipment, IrrigationBlock } from '../types/digitalTwin';
import EquipmentNode from '../components/digital-twin/EquipmentNode';
import IrrigationBlockNode from '../components/digital-twin/IrrigationBlockNode';
import DetailsPanel from '../components/digital-twin/DetailsPanel';
import { useIntlFormatting } from '../utils/intl';

const nodeTypes = { equipment: EquipmentNode, irrigationBlock: IrrigationBlockNode };
type Selection = { kind: 'equipment'; value: Equipment } | { kind: 'block'; value: IrrigationBlock };

// Internal cache namespace for the unauthenticated/loading state. Never
// rendered — used only to scope React Query keys by subject.
const ANONYMOUS_SUBJECT_KEY = 'anonymous';
// Internal cache namespace for a subject with no assigned organization. Never
// rendered — used only to scope React Query keys by organization.
const NO_ORG_KEY = 'no-org';

const getErrorKey = (error: unknown): { titleKey: string; detailKey: string } => {
  if (error instanceof DigitalTwinApiError) {
    if (error.status === 401) return { titleKey: 'errorSessionExpiredTitle', detailKey: 'errorSessionExpiredDetail' };
    if (error.status === 403) return { titleKey: 'errorUnauthorizedTitle', detailKey: 'errorUnauthorizedDetail' };
    if (error.status >= 500) return { titleKey: 'errorServiceUnavailableTitle', detailKey: 'errorServiceUnavailableDetail' };
  }
  return { titleKey: 'errorUnexpectedTitle', detailKey: 'errorUnexpectedDetail' };
};

const DigitalTwin: React.FC = () => {
  const { t } = useTranslation('digitalTwin');
  useIntlFormatting();

  const [farmId, setFarmId] = useState('');
  const [showSensors, setShowSensors] = useState(true);
  const [showBlocks, setShowBlocks] = useState(true);
  const [detailed, setDetailed] = useState(true);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [flow, setFlow] = useState<ReactFlowInstance<DigitalTwinNode, Edge> | null>(null);

  // Account-scoped React Query cache keys: subject A and subject B must never
  // share cached farm/telemetry data through the global QueryClient.
  const { user } = useAuth();
  const subjectId = user?.id ?? ANONYMOUS_SUBJECT_KEY;
  const orgId = user?.org_id ?? NO_ORG_KEY;
  const queryKeys = digitalTwinQueryKeys(subjectId, orgId, farmId);

  const farmsQuery = useQuery({ queryKey: queryKeys.farms, queryFn: digitalTwinApi.listFarms });
  const activeFarms = useMemo(
    () => (farmsQuery.data || []).filter((farm) => farm.status === 'active'),
    [farmsQuery.data],
  );

  useEffect(() => {
    if (!activeFarms.length) return;
    if (!activeFarms.some((farm) => farm.id === farmId)) setFarmId(activeFarms[0].id);
  }, [activeFarms, farmId]);

  useEffect(() => setSelection(null), [farmId]);

  const twinQuery = useQuery({
    queryKey: queryKeys.twin,
    enabled: Boolean(farmId),
    queryFn: async () => {
      const [blocks, equipment, sensors, links] = await Promise.all([
        digitalTwinApi.listIrrigationBlocks(farmId),
        digitalTwinApi.listEquipment(farmId),
        digitalTwinApi.listSensors(farmId),
        digitalTwinApi.listHydraulicLinks(farmId),
      ]);
      return { blocks, equipment, sensors, links };
    },
  });

  const telemetryQuery = useQuery({
    queryKey: queryKeys.telemetry,
    enabled: Boolean(farmId),
    refetchInterval: 30000,
    queryFn: async () => {
      const [nodes, alerts] = await Promise.all([
        iotApi.getJson<IoTNode[]>('/iot/nodes').catch(() => []),
        iotApi.getJson<IoTAlert[]>('/iot/alerts').catch(() => []),
      ]);
      return { nodes, alerts };
    },
  });

  const telemetryByEquipmentId = useMemo(() => buildEquipmentTelemetry({
    equipment: twinQuery.data?.equipment || [],
    sensors: twinQuery.data?.sensors || [],
    nodes: telemetryQuery.data?.nodes || [],
    alerts: telemetryQuery.data?.alerts || [],
    blocks: twinQuery.data?.blocks || [],
    links: twinQuery.data?.links || [],
    demo: { enabled: isDigitalTwinDemoTelemetryAllowed() },
  }), [twinQuery.data, telemetryQuery.data]);

  const graph = useMemo(() => buildDigitalTwinGraph({
    equipment: twinQuery.data?.equipment || [],
    blocks: twinQuery.data?.blocks || [],
    sensors: twinQuery.data?.sensors || [],
    links: twinQuery.data?.links || [],
    showSensors,
    showBlocks,
    detailed,
    telemetryByEquipmentId,
  }), [twinQuery.data, showSensors, showBlocks, detailed, telemetryByEquipmentId]);

  useEffect(() => {
    if (!flow || !graph.nodes.length) return;
    window.setTimeout(() => flow.fitView({ padding: 0.18, duration: 350 }), 0);
  }, [flow, farmId]);

  const reload = () => Promise.all([farmsQuery.refetch(), twinQuery.refetch(), telemetryQuery.refetch()]);
  const loading = farmsQuery.isLoading || (Boolean(farmId) && twinQuery.isLoading);
  const error = farmsQuery.error || twinQuery.error;
  const selectedFarm = activeFarms.find((farm) => farm.id === farmId);

  const onNodeClick: NodeMouseHandler<DigitalTwinNode> = (_event, node) => {
    if (node.data.kind === 'equipment') setSelection({ kind: 'equipment', value: node.data.equipment });
    else setSelection({ kind: 'block', value: node.data.block });
  };

  return (
    <div className="dt-page">
      <header className="dt-page-header">
        <div>
          <span className="dt-kicker">{t('pageKicker')}</span>
          <h1>{t('pageTitle')}</h1>
          <p>{t('pageSubtitle')}</p>
        </div>
        <label className="dt-farm-select">
          <span>{t('farmLabel')}</span>
          <select value={farmId} onChange={(event) => setFarmId(event.target.value)} disabled={!activeFarms.length}>
            {activeFarms.map((farm) => <option value={farm.id} key={farm.id}>{farm.name}</option>)}
          </select>
        </label>
      </header>

      <div className="dt-toolbar" aria-label={t('toolbarAriaLabel')}>
        <button onClick={() => flow?.fitView({ padding: 0.18, duration: 350 })}><Focus size={16} /> {t('fitView')}</button>
        <button onClick={reload} disabled={loading}><RefreshCw size={16} /> {t('reload')}</button>
        <span className="dt-toolbar-divider" />
        <button className={showSensors ? 'is-active' : ''} onClick={() => setShowSensors((value) => !value)}>
          {showSensors ? <Eye size={16} /> : <EyeOff size={16} />} {t('sensors')}
        </button>
        <button className={showBlocks ? 'is-active' : ''} onClick={() => setShowBlocks((value) => !value)}>
          <Layers3 size={16} /> {t('blocks')}
        </button>
        <button className={detailed ? 'is-active' : ''} onClick={() => setDetailed((value) => !value)}>
          <Rows3 size={16} /> {detailed ? t('detailed') : t('compact')}
        </button>
        {twinQuery.data && (
          <span className="dt-topology-count">
            {t('topologySummary', {
              equipment: t('equipmentCount', { count: twinQuery.data.equipment.length }),
              links: t('linkCount', { count: twinQuery.data.links.length }),
            })}
          </span>
        )}
      </div>

      <main className="dt-workspace">
        <section className="dt-canvas" aria-label={t('canvasAriaLabel', { farmName: selectedFarm?.name || '' })}>
          {loading ? (
            <div className="dt-state"><LoaderCircle className="dt-spin" /><h2>{t('loadingTitle')}</h2><p>{t('loadingDetail')}</p></div>
          ) : error ? (
            <div className="dt-state dt-state--error"><TriangleAlert /><h2>{t(getErrorKey(error).titleKey)}</h2><p>{t(getErrorKey(error).detailKey)}</p><button onClick={reload}>{t('retry')}</button></div>
          ) : !activeFarms.length ? (
            <div className="dt-state"><Layers3 /><h2>{t('noActiveFarmTitle')}</h2><p>{t('noActiveFarmDetail')}</p></div>
          ) : !twinQuery.data?.equipment.length ? (
            <div className="dt-state"><Layers3 /><h2>{t('emptyTwinTitle')}</h2><p>{t('emptyTwinDetail')}</p></div>
          ) : (
            <ReactFlow<DigitalTwinNode, Edge>
              nodes={graph.nodes}
              edges={graph.edges}
              nodeTypes={nodeTypes}
              onInit={setFlow}
              onNodeClick={onNodeClick}
              onPaneClick={() => setSelection(null)}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable
              minZoom={0.25}
              maxZoom={1.8}
              fitView
              proOptions={{ hideAttribution: false }}
            >
              <Background variant={BackgroundVariant.Dots} gap={24} size={1.3} color="#bdd4d7" />
              <MiniMap pannable zoomable nodeStrokeWidth={3} className="dt-minimap" />
            </ReactFlow>
          )}
        </section>
        <DetailsPanel
          selection={selection}
          equipment={twinQuery.data?.equipment || []}
          sensors={twinQuery.data?.sensors || []}
          links={twinQuery.data?.links || []}
          telemetryByEquipmentId={telemetryByEquipmentId}
          onClose={() => setSelection(null)}
        />
      </main>
    </div>
  );
};

export default DigitalTwin;
